import React, { useState } from "react";
import useMetrics from "@/hooks/useMetrics";
import { MetricsTabs } from "@/components/MetricsTabs";
import { Button } from "@/components/ui/button";
import { CategoriesGrid } from "@/components/CategoriesGrid";
import { useCategoryContributions } from "@/hooks/useCategoryContributions";
import { RankingDisplay } from "@/components/RankingDisplay";
import { useRanking } from "@/context/RankingContext";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * This page replaces the "Metrics" tab.
 * We display the "MetricsTabs" plus controls for resetting, toggling simulation, etc.
 */

export function MetricsPage() {
  const [activeTab, setActiveTab] = useState("outcomes");
  const { 
    isSimulationMode, 
    simulatedScore, 
    setSimulatedScore,
    toggleSimulationMode: contextToggleSimulation,
  } = useRanking();

  const {
    currentMetrics,
    handleMetricChange,
    handleReset,
    isLoading: metricsLoading,
    originalMetrics,
  } = useMetrics();

  // Get category contributions (now fixed weights)
  const categoryContributions = useCategoryContributions();

  if (!currentMetrics || metricsLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-[#4FAADD]/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center h-32">
              <p className="text-base text-[#4478B9]">Loading metrics...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // For demo, let's assume "originalScore" is from originalMetrics if available:
  const originalScore = originalMetrics?.outcomes?.[0]?.value
    ? 55.0
    : 55.0; // Hard-coded fallback

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-8">
        {/* Left Column - Main Content */}
        <div className="col-span-12 md:col-span-9 space-y-8">
          {/* Category Contributions */}
          <Card className="border-[#4FAADD]/20">
            <CardContent className="p-6">
              <h2 className="text-2xl font-semibold mb-2 text-[#274791]">Methodology Breakdown</h2>
              <p className="text-base text-[#4478B9] mb-4">
                See how different factors contribute to the overall ranking
              </p>
              <CategoriesGrid categories={categoryContributions} />
            </CardContent>
          </Card>

          {/* Metrics Controls */}
          <Card className="border-[#4FAADD]/20">
            <CardContent className="p-6">
              <h2 className="text-2xl font-semibold mb-2 text-[#274791]">Adjust Metrics</h2>
              <p className="text-base text-[#4478B9] mb-4">
                Modify individual metrics to see their impact on the overall ranking
              </p>
              <MetricsTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                currentMetrics={currentMetrics}
                handleMetricChange={handleMetricChange}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Controls */}
        <Card className="col-span-12 md:col-span-3 border-[#4FAADD]/20">
          <CardContent className="p-6">
            <h2 className="text-2xl font-semibold mb-4 text-[#274791]">Simulation Controls</h2>
            {/* Score Display */}
            {simulatedScore !== null && (
              <div className="bg-[#4FAADD]/10 px-4 py-3 rounded-lg mb-6 border border-[#4FAADD]/20">
                <span className="text-base font-medium text-[#274791]">Simulated Score: </span>
                <span
                  className={cn(
                    "text-lg font-bold",
                    simulatedScore > originalScore
                      ? "text-[#84CBBF]"  // Turquoise for positive
                      : simulatedScore < originalScore
                      ? "text-[#D62B2A]"  // Red for negative
                      : "text-[#4478B9]"  // Medium blue for neutral
                  )}
                >
                  {simulatedScore.toFixed(1)}
                </span>
                <span className="text-base ml-1 text-[#4478B9]">
                  ({simulatedScore > originalScore ? "+" : ""}
                  {(simulatedScore - originalScore).toFixed(1)})
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={contextToggleSimulation}
                      className={cn(
                        "w-full text-white",
                        isSimulationMode
                          ? "bg-[#E36D34] hover:bg-[#E36D34]/90"  // Orange for exit
                          : "bg-[#FCE58E] hover:bg-[#FCE58E]/90 text-[#274791]"   // Yellow for start
                      )}
                    >
                      <span className="text-base">
                        {isSimulationMode ? "Exit Simulation" : "Start Simulation"}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#274791] text-white">
                    <p className="text-base">
                      {isSimulationMode 
                        ? "Return to viewing current metrics" 
                        : "View how your adjusted metrics affect the ranking"}
                    </p>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleReset();
                        setSimulatedScore(null);
                      }}
                      className="w-full mt-3 border-[#4FAADD] text-[#274791] hover:bg-[#4FAADD]/10 hover:text-[#274791]"
                    >
                      <span className="text-base">Reset to Default</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#274791] text-white">
                    <p className="text-base">Reset all metrics to their original values</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Current Ranking */}
            <div className="pt-8 border-t border-[#4FAADD]/20 mt-6">
              <h3 className="text-lg font-medium mb-4 text-[#274791] text-center">Ranking</h3>
              <RankingDisplay />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}